// pages/master/menu/PackageMenuPage.jsx
'use client';

import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  IconButton,
  TablePagination,
  TextField,
  InputAdornment,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LayersIcon from '@mui/icons-material/Layers';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';

import { deletePackageMenu, usePackageList, revalidatePackageList } from '../../../stores/roleStore';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import PackageMenuAddEdit from './PackageMenuAddEdit';
import ListPackageMenuDialog from './ListPackageMenuDialog';
import { useLanguage } from '../../../utils/i18n';
import { useNotifier } from '../../../components/Notifier';
const PackageMenuPage = () => {
  const notify = useNotifier();
  const { t } = useLanguage();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [openListDialog, setOpenListDialog] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  const { data: displayedData, totalRecord, isLoading: loading } = usePackageList(page, rowsPerPage, 'package_name', searchTerm);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAdd = () => {
    setEditingItem(null);
    setOpenDialog(true);
  };

  const handleEdit = (e, item) => {
    e.stopPropagation();
    setEditingItem(item);
    setOpenDialog(true);
  };

  const handleDeleteClick = (e, id, itemName) => {
    e.stopPropagation();
    setConfirmId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!confirmId) return;
    setConfirmLoading(true);
    try {
      await deletePackageMenu(confirmId);
      notify({ message: t('packageMenu.packageDeletedSuccess', 'Package successfully deleted'), severity: 'success' });

      // Cek dengan displayedData agar sesuai dengan item yang dirender saat ini
      if (displayedData.length === 1 && page > 0) {
        setPage((prev) => prev - 1);
      } else {
        revalidatePackageList();
      }
    } catch (err) {
      console.error(err);
      notify({ message: t('packageMenu.packageDeletedFailed', 'Failed to delete package'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmId(null);
      setConfirmName('');
    }
  };

  const handleRowClick = (item) => {
    setSelectedPackage(item);
    setOpenListDialog(true);
  };

  return (
    <Box sx={{ width: '100%', pb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <TextField
          size="small"
          placeholder={t('packageMenu.searchPackage', 'Search package...')}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          sx={{ width: { xs: '100%', sm: 300 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          disableElevation
          onClick={handleAdd}
          sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2, px: 3 }}
        >
          {t('packageMenu.addPackage', 'Add Package')}
        </Button>
      </Box>

      <Paper elevation={0} variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}>
            <CircularProgress />
          </Box>
        ) : displayedData.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
            <LayersIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
            <Typography>{t('packageMenu.noPackagesFound', 'No packages found. Click "Add Package" to create one.')}</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* PASTIKAN MENGGUNAKAN displayedData DI SINI */}
            {displayedData.map((item, index) => (
              <Box
                key={item.id}
                onClick={() => handleRowClick(item)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  px: 3,
                  borderBottom: index < displayedData.length - 1 ? '1px solid #f0f0f0' : 'none',
                  transition: 'background-color 0.2s ease',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: '#f8fafc' }
                }}
              >
                <Box sx={{ color: '#9e9e9e', display: 'flex', alignItems: 'center', mr: 2 }}>
                  <LayersIcon />
                </Box>

                <Typography variant="body1" fontWeight="500" sx={{ flex: 1, color: '#333' }}>
                  {item.package_name}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FormatListBulletedIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowClick(item);
                    }}
                    sx={{ textTransform: 'none', borderRadius: 1.5, mr: 1 }}
                  >
                    {t('packageMenu.manageMenus', 'Manage Menus')}
                  </Button>

                  {item.package_name !== 'Enterprise' && item.package_name !== 'Admin' && (
                    <>
                      <Tooltip title={t('packageMenu.editPackage', 'Edit Package')}>
                        <IconButton
                          size="small"
                          variant="contained"
                          disableElevation
                          onClick={(e) => handleEdit(e, item)}
                          sx={{
                            textTransform: 'none',
                            bgcolor: '#efc550',
                            color: 'white',
                            minWidth: 0,
                            px: 2,
                            borderRadius: 1.5,
                            '&:hover': { bgcolor: '#e0b848' }
                          }}
                        >
                          <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={t('packageMenu.deletePackage', 'Delete Package')}>
                        <IconButton
                          size="small"
                          variant="contained"
                          disableElevation
                          onClick={(e) => handleDeleteClick(e, item.id, item.package_name)}
                          sx={{
                            textTransform: 'none',
                            bgcolor: '#ef5350',
                            color: 'white',
                            minWidth: 0,
                            px: 2,
                            borderRadius: 1.5,
                            '&:hover': { bgcolor: '#e53935' }
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Box>
            ))}

            <TablePagination
              component="div"
              count={totalRecord}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: '1px solid #f0f0f0' }}
            />
          </Box>
        )}
      </Paper>

      {openDialog && (
        <PackageMenuAddEdit
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          isEdit={!!editingItem}
          initialData={editingItem}
          onSuccess={revalidatePackageList}
        />
      )}

      {openListDialog && selectedPackage && (
        <ListPackageMenuDialog open={openListDialog} onClose={() => setOpenListDialog(false)} packageData={selectedPackage} />
      )}

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={() => setConfirmOpen(false)}
        itemName={confirmName}
        onConfirm={handleConfirmDelete}
        title={t('packageMenu.deletePackage', 'Delete Package')}
        message={t('packageMenu.confirmDeletePackage', 'Are you sure you want to delete this package? This action cannot be undone.')}
        confirmLabel={t('general.delete', 'Delete')}
        cancelLabel={t('general.cancel', 'Cancel')}
      />
    </Box>
  );
};

export default PackageMenuPage;
